// @flow

import * as React from 'react';

import * as Import from '@ta/platform.import';
import type { AuthZInfo } from '@ta/common.authz';
import { ErrorBoundary } from '@ta/common.error-handling';
import { featureIsEnabled } from '@ta/common.features';
import { getObjectId } from '@ta/common.global';
import { IsVisibleOnce } from '@ta/common.is-visible';
import Logging from '@ta/common.logging';
import { RouteConsumer } from '@ta/common.routing';
import trackEventAJAX from '@ta/common.tracking';
import type { ContentType } from '@ta/social.content-removal';
import { InsightProfileExplicitPreferencesTripSetup, LocationOffers } from '@ta/social.offers';
import {
  ReviewSection,
  PhotoSection,
  VideoSection,
  ForumsListSection,
  ForumPostSection,
  LinkPostSection,
  RepostSection,
  TripSection,
  GeoOverviewSection,
  ManagedMediaBatchSection,
} from '@ta/social.sections';
import { Shelf } from '@ta/social.shelves';
import { FollowShelf } from '@ta/social.suggested-followees';
import { ItemIdContext } from '@ta/tracking.interactions';

// this component never shows up on unscoped homepages, so only pull it when we need it.
const DfpAdSection = Import.component.onUsage(() => import('@ta/social.sections'), 'DfpAdSection');

type ItemRenderContext = {|
  mixerPlacement: string,
  puid: ?string,
  locationId: number,
  viewportCategory: string,
  member: any,
  section: any,
  actor: any,
  propsItem: any,
  sectionId: string,
  deleteObjectFunc: (ContentType, string) => mixed,
  loggedInMemberAuthorization?: AuthZInfo,
  untagObjectFunc: any => void,
  loggedInUserId: string,
  dmoCompetitorTracker: boolean,
|};

const DESKTOP_SHELF_ITEMS = 3;
const MOBILE_SHELF_ITEMS = 20;

/**
 * A couple of notes about this method:
 * 1. it's guaranteed to receive sections with non-empty item list
 * 2. it's ugly and needs to be consolidated between item level and section level processing and split into multiple
 *    "renderer" files per each item/section type.  But it's kept in the current state to reduce the risk of
 *    breaking things before social launch.  Tracked in https://jira.tamg.io/browse/TRVX-9878
 */
const renderItem = ({
  mixerPlacement,
  puid,
  locationId,
  viewportCategory,
  member,
  section,
  actor,
  propsItem,
  deleteObjectFunc,
  loggedInMemberAuthorization,
  untagObjectFunc,
  loggedInUserId,
  dmoCompetitorTracker,
}: ItemRenderContext) => {
  let componentSections;
  const obj = propsItem.object;

  const deleteObjectBound = () => {
    deleteObjectFunc(obj.__typename, getObjectId(obj));
  };
  const untagObjectBound = () => untagObjectFunc(obj);

  if (section?.type === 'MIXED_CURATED' || section?.type === 'DMO_SPONSORED') {
    componentSections = (
      <Shelf
        minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
        maxListItems={viewportCategory === 'MOBILE' ? Infinity : DESKTOP_SHELF_ITEMS}
        shelf={section}
        locationId={locationId}
        mixerPlacement={mixerPlacement}
        puid={puid}
        key={section.id}
        sponsorship={section.sponsorship}
      />
    );
  }

  if (!componentSections) {
    switch (propsItem.object.__typename) {
      case 'Review':
        componentSections = propsItem.object?.reviewStatus !== 'HOLD' && (
          <ReviewSection
            key={propsItem.object.id}
            review={propsItem}
            isSingleObject
            member={member}
            actor={actor}
            section={section}
            deleteObject={deleteObjectBound}
            loggedInMemberAuthorization={loggedInMemberAuthorization}
            layout="FULL"
            clusterId={section.clusterId}
          />
        );
        break;
      case 'Photo':
        componentSections = (
          <PhotoSection
            key={propsItem.object.id}
            photo={propsItem}
            isSingleObject
            member={member}
            actor={actor}
            deleteObject={deleteObjectBound}
            loggedInMemberAuthorization={loggedInMemberAuthorization}
            untagObject={untagObjectBound}
            loggedInUserId={loggedInUserId}
            layout="FULL"
            clusterId={section.clusterId}
            sponsorship={section.sponsorship}
          />
        );
        break;
      case 'MediaBatch': {
        const nonnullMedia = propsItem.object.media.filter(Boolean);
        if (nonnullMedia.length > 0) {
          componentSections = (
            <ManagedMediaBatchSection
              metadata={propsItem.object}
              photos={nonnullMedia}
              isSingleObject
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              untagObject={untagObjectBound}
              layout="FULL"
              sponsorship={section.sponsorship}
            />
          );
        }
        break;
      }
      case 'Video':
        componentSections = (
          <VideoSection
            key={propsItem.object.id}
            video={propsItem}
            isSingleObject
            member={member}
            actor={actor}
            deleteObject={deleteObjectBound}
            loggedInMemberAuthorization={loggedInMemberAuthorization}
            untagObject={untagObjectBound}
            loggedInUserId={loggedInUserId}
            clusterId={section.clusterId}
            sponsorship={section.sponsorship}
          />
        );
        break;
      case 'ForumPost':
        componentSections = (
          <RouteConsumer>
            {({ route }) => (
              <React.Fragment>
                {route.tab === 'forum' ? (
                  <ForumsListSection key={propsItem.object.id} post={propsItem} />
                ) : (
                  <ForumPostSection
                    key={propsItem.object.id}
                    post={propsItem}
                    isSingleObject
                    member={member}
                    actor={actor}
                    deleteObject={deleteObjectBound}
                    loggedInMemberAuthorization={loggedInMemberAuthorization}
                    clusterId={section.clusterId}
                  />
                )}
              </React.Fragment>
            )}
          </RouteConsumer>
        );
        break;
      case 'LinkPost':
        componentSections = (
          <LinkPostSection
            key={propsItem.object.postId}
            post={propsItem}
            isSingleObject
            member={member}
            actor={actor}
            deleteObject={deleteObjectBound}
            loggedInMemberAuthorization={loggedInMemberAuthorization}
            untagObject={untagObjectBound}
            loggedInUserId={loggedInUserId}
            clusterId={section.clusterId}
            sponsorship={section.sponsorship}
          />
        );
        break;
      case 'Trip':
        // Breaking out in case of a shelf for list of trips
        // to avoid executing the code (for the shelf) since the items inside the shelf are of the TRIP
        if (section.type === 'TRIPS') {
          break;
        }
        // checking features here because trips can show up in your activity feed even when you don't ask for them
        if (
          propsItem.object.tripStatus !== 'PUBLIC' ||
          !featureIsEnabled('trips_2018_home_profile')
        ) {
          // don't show it
          componentSections = <span />;
          break;
        }

        componentSections = (
          <TripSection
            key={propsItem.object.id}
            trip={propsItem}
            member={member}
            actor={actor}
            deleteObject={deleteObjectBound}
            untagObject={untagObjectBound}
            loggedInUserId={loggedInUserId}
            isSingleObject
            clusterId={section.clusterId}
            sponsorship={section.sponsorship}
          />
        );
        break;
      case 'Repost':
        componentSections = (
          <RepostSection
            key={propsItem.object.repostId}
            repost={propsItem}
            isSingleObject
            member={member}
            actor={actor}
            deleteObject={deleteObjectBound}
            loggedInMemberAuthorization={loggedInMemberAuthorization}
            untagObject={untagObjectBound}
            loggedInUserId={loggedInUserId}
            clusterId={section.clusterId}
          />
        );
        break;
      default:
        // Swtich below will pick this up
        break;
    }
  }

  // Accommodation prices
  const isAccommodation = section.items.filter(
    item =>
      item && item.object && item.object.placeType && item.object.placeType === 'ACCOMMODATION',
  );
  const accommodationIds =
    isAccommodation && isAccommodation.length > 0
      ? isAccommodation.map(i => i).map(ii => ii.object && ii.object.locationId)
      : [];
  const hasAccommodations = accommodationIds && accommodationIds.length > 0;

  // TRVX-13935: Display prices on feed (Responsive web)
  const showAccommodationPrices =
    featureIsEnabled('accommodation_prices_in_shelves') && locationId !== 1;

  if (!componentSections) {
    switch (section.type) {
      case 'LOCATION_LIST':
        // Just query for accommodations - anything else queries unnecessarily
        if (showAccommodationPrices && hasAccommodations) {
          const offerStrategy = featureIsEnabled('availabilityCheckStrategy_oracleDefault')
            ? 'oracle_default'
            : 'oracle_default:oracle_only';

          componentSections = (
            <InsightProfileExplicitPreferencesTripSetup>
              {insightProfile => (
                <LocationOffers
                  insightProfile={insightProfile}
                  locationIds={accommodationIds}
                  availabilityCheckStrategy={offerStrategy}
                >
                  {locations => (
                    <Shelf
                      minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
                      maxListItems={viewportCategory === 'MOBILE' ? Infinity : DESKTOP_SHELF_ITEMS}
                      shelf={section}
                      locationId={locationId}
                      mixerPlacement={mixerPlacement}
                      puid={puid}
                      key={section.id}
                      sponsorship={section.sponsorship}
                      locationOffers={locations}
                    />
                  )}
                </LocationOffers>
              )}
            </InsightProfileExplicitPreferencesTripSetup>
          );
        } else {
          componentSections = (
            <Shelf
              minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
              maxListItems={viewportCategory === 'MOBILE' ? Infinity : DESKTOP_SHELF_ITEMS}
              shelf={section}
              locationId={locationId}
              mixerPlacement={mixerPlacement}
              puid={puid}
              key={section.id}
              sponsorship={section.sponsorship}
            />
          );
        }
        break;

      case 'BRAND_MESSAGING':
        // brand messaging not supported for launch
        break;
      case 'LOCATION_FILTERS':
      case 'CRUISE_SEARCH_ITEM_LIST':
      case 'FLIGHTS_DESTINATION_ITEM_LIST':
      case 'PICK_UP_WHERE_YOU_LEFT_OFF':
      case 'ATTRACTION_PRODUCT_LIST':
        componentSections = (
          <Shelf
            minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
            maxListItems={viewportCategory === 'MOBILE' ? Infinity : DESKTOP_SHELF_ITEMS}
            shelf={section}
            locationId={locationId}
            mixerPlacement={mixerPlacement}
            puid={puid}
            key={section.id}
            sponsorship={section.sponsorship}
          />
        );
        break;
      case 'RENTAL_CARS': {
        const containsInvalidPrices =
          section.items.filter(item => !item.object.rentalCarsShelfPrice).length > 0;
        if (!containsInvalidPrices) {
          componentSections = (
            <Shelf
              minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
              maxListItems={viewportCategory === 'MOBILE' ? Infinity : DESKTOP_SHELF_ITEMS}
              shelf={section}
              locationId={locationId}
              mixerPlacement={mixerPlacement}
              puid={puid}
              key={section.id}
              sponsorship={section.sponsorship}
            />
          );
        }
        break;
      }
      case 'PHOTO':
        componentSections = (
          <ItemIdContext.Provider value={{ itemId: section.items[0].id }}>
            <PhotoSection
              photo={section.items[0]}
              isSingleObject
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              loggedInMemberAuthorization={loggedInMemberAuthorization}
              untagObject={untagObjectBound}
              loggedInUserId={loggedInUserId}
              layout="FULL"
              clusterId={section.clusterId}
              sponsorship={section.sponsorship}
            />
          </ItemIdContext.Provider>
        );
        break;
      case 'MEDIA_BATCH': {
        const nonnullMedia =
          (section.items?.[0]?.media && section.items[0].media.filter(Boolean)) || [];
        if (nonnullMedia.length > 0) {
          componentSections = (
            <ManagedMediaBatchSection
              metadata={section.items[0]}
              photos={nonnullMedia}
              isSingleObject
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              untagObject={untagObjectBound}
              layout="FULL"
              sponsorship={section.sponsorship}
            />
          );
        }
        break;
      }
      case 'REVIEW':
        componentSections = section.items[0].object?.reviewStatus !== 'HOLD' && (
          <ItemIdContext.Provider value={{ itemId: section.items[0].id }}>
            <ReviewSection
              review={section.items[0]}
              isSingleObject
              member={member}
              actor={actor}
              section={section}
              deleteObject={deleteObjectBound}
              loggedInMemberAuthorization={loggedInMemberAuthorization}
              layout="FULL"
              clusterId={section.clusterId}
            />
          </ItemIdContext.Provider>
        );
        break;

      case 'VIDEOS':
        componentSections = (
          <Shelf
            minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
            maxListItems={viewportCategory === 'MOBILE' ? MOBILE_SHELF_ITEMS : DESKTOP_SHELF_ITEMS}
            shelf={section}
            locationId={locationId}
            mixerPlacement={mixerPlacement}
            puid={puid}
            member={member}
            key={section.id}
            shelfKey={section.id}
            sponsorship={section.sponsorship}
          />
        );
        break;

      case 'VIDEO':
        componentSections = (
          <ItemIdContext.Provider value={{ itemId: section.items[0].id }}>
            <VideoSection
              video={section.items[0]}
              isSingleObject
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              loggedInMemberAuthorization={loggedInMemberAuthorization}
              untagObject={untagObjectBound}
              loggedInUserId={loggedInUserId}
              clusterId={section.clusterId}
              sponsorship={section.sponsorship}
            />
          </ItemIdContext.Provider>
        );
        break;

      case 'LINK_POST':
        componentSections = (
          <ItemIdContext.Provider value={{ itemId: section.items[0].id }}>
            <LinkPostSection
              post={section.items[0]}
              isSingleObject
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              loggedInMemberAuthorization={loggedInMemberAuthorization}
              untagObject={untagObjectBound}
              loggedInUserId={loggedInUserId}
              clusterId={section.clusterId}
              sponsorship={section.sponsorship}
            />
          </ItemIdContext.Provider>
        );
        break;

      case 'REPOST':
        componentSections = (
          <ItemIdContext.Provider value={{ itemId: section.items[0].id }}>
            <RepostSection
              repost={section.items[0]}
              isSingleObject
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              loggedInMemberAuthorization={loggedInMemberAuthorization}
              untagObject={untagObjectBound}
              loggedInUserId={loggedInUserId}
              clusterId={section.clusterId}
            />
          </ItemIdContext.Provider>
        );
        break;

      case 'PROFILE_LIST':
        if (section.items.length > 2) {
          componentSections = (
            <FollowShelf
              isSingleObject
              suggestedFollowees={section.items}
              title={section.title}
              shelfId={section.id}
              clusterId={section.clusterId}
            />
          );
        } else {
          Logging.error('got a PROFILE_LIST type with not enough profiles');
        }

        break;

      case 'TRIPS':
        componentSections = (
          <Shelf
            minListItems={viewportCategory === 'MOBILE' ? 0 : DESKTOP_SHELF_ITEMS}
            maxListItems={viewportCategory === 'MOBILE' ? MOBILE_SHELF_ITEMS : DESKTOP_SHELF_ITEMS}
            shelf={section}
            locationId={locationId}
            mixerPlacement={mixerPlacement}
            puid={puid}
            member={member}
            key={section.id}
            shelfKey={section.id}
            sponsorship={section.sponsorship}
          />
        );
        break;

      case 'TRIP':
        if (
          section.items[0] &&
          section.items[0].object &&
          section.items[0].object.tripStatus !== 'PUBLIC'
        ) {
          Logging.error(
            `Got a ${section.items[0].object.tripStatus} trip with id ${
              section.items[0].object.id
            } when trying to create TripSection.`,
          );
          break;
        }

        componentSections = (
          <ItemIdContext.Provider value={{ itemId: section.items[0].id }}>
            <TripSection
              trip={section.items[0]}
              member={member}
              actor={actor}
              deleteObject={deleteObjectBound}
              untagObject={untagObjectBound}
              loggedInUserId={loggedInUserId}
              isSingleObject
              clusterId={section.clusterId}
              sponsorship={section.sponsorship}
            />
          </ItemIdContext.Provider>
        );
        break;

      case 'GEO_OVERVIEW':
        componentSections = <GeoOverviewSection overview={section.items[0]} />;
        break;
      case 'DFP_AD':
        // Keyword hard-coded until we add variance, and decide how we
        // want to input that variance
        componentSections = (
          <DfpAdSection
            keyword="DMO_COMPETITOR"
            locationId={locationId}
            clusterId={section.clusterId}
            feedSectionId={section.id}
          />
        );
        break;
      default:
        /**
         * @todo: render other non-shelf types that mixer returns
         */
        Logging.warn(`unimplemented section type: ${section.type}`);
        break;
    }
  }

  if (dmoCompetitorTracker && componentSections) {
    componentSections = (
      <IsVisibleOnce
        onIsVisibleCallback={() => {
          trackEventAJAX({
            module: 'dmocompshelfeligible',
            action: 'in_view',
          });
        }}
      >
        {componentSections}
      </IsVisibleOnce>
    );
  }

  return <ErrorBoundary>{componentSections || null}</ErrorBoundary>;
};

renderItem.defaultProps = {
  loggedInMemberAuthorization: undefined,
};

export default renderItem;
